/* SSi website – Pay / Start-your-trial confirmation page.
 *
 * Step 4 uses Stripe's "Embedded Components" integration:
 *   1. The route fetches a Checkout Session client_secret from the backend
 *      (POST /membership/onestep/:product_id with elements: true).
 *   2. PayCheckoutForm calls stripe.initCheckoutElementsSdk({ clientSecret })
 *      and mounts the resulting PaymentElement to a div ref.
 *   3. On submit, sdk.loadActions() → actions.confirm() finalises the session;
 *      Stripe redirects to the return_url configured by the backend.
 *
 * Stripe Tax is handled automatically by the Checkout Session — no additional
 * frontend code is required for tax display or calculation.
 *
 * Required env vars (see .env.example):
 *   VITE_STRIPE_PUBLISHABLE_KEY  — pk_test_… or pk_live_…
 *   VITE_API_BASE                — backend origin, e.g. https://api.saysomethingin.com
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { markHasAccount } from '../account/hasAccount.js';

// Initialised once at module level so Stripe.js is only fetched once.
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');

export const PAY_LANGS = [
  { code:'en-es',       name:'Spanish',    flag:'spanish',    greet:'Hola' },
  { code:'en-fr',       name:'French',     flag:'french',     greet:'Bonjour' },
  { code:'en-de',       name:'German',     flag:'german',     greet:'Hallo' },
  { code:'en-it',       name:'Italian',    flag:'italian',    greet:'Ciao' },
  { code:'en-pt',       name:'Portuguese', flag:'portuguese', greet:'Olá' },
  { code:'en-cmn',      name:'Mandarin',   flag:'chinese',    greet:'你好' },
  { code:'en-ja',       name:'Japanese',   flag:'japanese',   greet:'こんにちは' },
  { code:'en-ko',       name:'Korean',     flag:'korean',     greet:'안녕' },
  { code:'en-ar',       name:'Arabic',     flag:'arabic',     greet:'مرحبا' },
  { code:'en-cy-south', name:'Welsh',      flag:'wales',      greet:'Shwmae' },
];

const FLAG_ASSETS = ['spanish','french','german','italian','portuguese','chinese','japanese','wales','ireland','korean','arabic'];

// PayHero renders steps 1–3. Step 4 + submit are injected via checkoutSlot
// so that the Stripe SDK can be initialised only once a client_secret is
// available, while steps 1–3 remain interactive during the fetch.
export function PayHero({ t, courseIdx, setCourseIdx, plan, setPlan, email, onEmailChange, emailStatus, onEmailBlur, checkoutSlot, offerPay = null }) {
  const lang = courseIdx >= 0 ? PAY_LANGS[courseIdx] : null;

  // Offer-test flags (Design handoff 2026-06-11). Control: plans shown,
  // steps numbered 1–4, prices £15/£150. Variants can hide the plan step
  // (steps renumber) and re-price the plan cards.
  const showPlans = !offerPay || offerPay.showPlans;
  const accountStepNum = showPlans ? 3 : 2;
  const planMonthly = (offerPay && offerPay.planMonthly) || { price: '£15',  per: '/month' };
  const planAnnual  = (offerPay && offerPay.planAnnual)  || { price: '£150', per: '/year'  };
  // Timeline row 3 price suffix is a tweak so variants can blank it or
  // bake the price into the title itself.
  const tl3Title = showPlans && plan === 'annual'
    ? [t.payTl3TitleA, t.payTl3PriceA ?? '£150'].filter(Boolean).join(' ')
    : [t.payTl3TitleM, t.payTl3PriceM ?? '£15'].filter(Boolean).join(' ');
  const tl3Body = showPlans && plan === 'annual' ? t.payTl3BodyA : t.payTl3BodyM;

  return (
    <section className="ssi-pay__wrap" style={payStyles.wrap}>
      <div className="ssi-pay__shell" style={payStyles.shell}>

        {/* LEFT – the form / selections */}
        <div className="ssi-pay__left" style={payStyles.left}>
          <div style={payStyles.kicker}>{t.payKicker}</div>
          <h1 style={payStyles.h1}>
            {lang
              ? <>{t.payH1Part1} <span style={{color:'var(--brandRed)'}}>{lang.name}</span>.</>
              : t.payH1Empty}
          </h1>
          <p style={payStyles.lede}>{t.payLede}</p>

          {/* Coupon banner (offer v4) – rendered only when the variant
            * provides payCouponStrong/payCouponRest tweaks. */}
          {t.payCouponStrong && (
            <div className="ssi-pay__coupon" style={payStyles.couponBanner} role="status">
              <span style={payStyles.couponTag}>{t.payCouponStrong}</span>
              <span style={payStyles.couponText}>{t.payCouponRest}</span>
            </div>
          )}

          {/* Step 1 – language */}
          <div style={payStyles.step}>
            <div style={payStyles.stepHead}>
              <div style={payStyles.stepNum}>1</div>
              <div>
                <div style={payStyles.stepTitle}>{t.payStep1Title}</div>
                <div style={payStyles.stepSub}>{t.payStep1Sub}</div>
              </div>
            </div>
            <div className="ssi-pay__picker" style={payStyles.picker}>
              {PAY_LANGS.map((L, idx) => {
                const active = idx === courseIdx;
                const flagExt = L.flag === 'wales' ? 'png' : 'svg';
                const flagAsset = FLAG_ASSETS.includes(L.flag)
                  ? `/assets/flag-${L.flag}.${flagExt}?v=4` : null;
                return (
                  <button
                    key={L.code}
                    type="button"
                    onClick={() => setCourseIdx(idx)}
                    style={{
                      ...payStyles.chip,
                      transform: active ? 'translateY(-2px)' : 'none',
                    }}>
                    <span style={{
                      ...payStyles.chipFlag,
                      boxShadow: active
                        ? '0 0 0 3px var(--brandRed), 0 6px 16px rgba(178,25,45,.28)'
                        : '0 0 0 1px rgba(30,20,18,.08), 0 1px 2px rgba(30,20,18,.06)',
                    }}>
                      {flagAsset
                        ? <img src={flagAsset} alt="" style={payStyles.chipFlagImg}/>
                        : <span style={payStyles.chipGlyph}>{L.greet}</span>}
                    </span>
                    <span style={{
                      ...payStyles.chipName,
                      color: active ? 'var(--brandRed)' : 'var(--fg-1)',
                      fontWeight: active ? 600 : 500,
                    }}>{L.name}</span>
                  </button>
                );
              })}
            </div>
            <div style={payStyles.stepNote}>{t.payStep1Note}</div>
          </div>

          {/* Step 2 – plan (hidden on single-plan offer variants) */}
          {showPlans && (
          <div style={payStyles.step}>
            <div style={payStyles.stepHead}>
              <div style={payStyles.stepNum}>2</div>
              <div>
                <div style={payStyles.stepTitle}>{t.payStep2Title}</div>
                <div style={payStyles.stepSub}>{t.payStep2Sub}</div>
              </div>
            </div>
            <div className="ssi-pay__planGrid" style={payStyles.planGrid}>
              {[
                { code:'monthly', name:t.payPlanMonthlyName,  price:planMonthly.price, per:planMonthly.per, blurb:t.payPlanMonthlyBlurb, save:null },
                { code:'annual',  name:t.payPlanAnnualName,   price:planAnnual.price,  per:planAnnual.per,  blurb:t.payPlanAnnualBlurb,  save:t.payPlanAnnualSave },
              ].map(p => {
                const active = plan === p.code;
                return (
                  <button
                    key={p.code}
                    type="button"
                    onClick={() => setPlan(p.code)}
                    style={{
                      ...payStyles.planCard,
                      borderColor: active ? 'var(--brandRed)' : 'var(--border)',
                      boxShadow: active ? '0 0 0 3px rgba(178,25,45,.15), 0 6px 16px rgba(178,25,45,.12)' : 'none',
                    }}>
                    <div style={payStyles.planTop}>
                      <div style={payStyles.planName}>{p.name}</div>
                      {p.save && <span style={payStyles.planSave}>{p.save}</span>}
                    </div>
                    <div style={payStyles.planPrice}>
                      <span style={payStyles.planPriceNum}>{p.price}</span>
                      <span style={payStyles.planPricePer}>{p.per}</span>
                    </div>
                    <div style={payStyles.planBlurb}>{p.blurb}</div>
                    <div style={{
                      ...payStyles.planRadio,
                      borderColor: active ? 'var(--brandRed)' : 'var(--border-strong)',
                    }}>
                      {active && <div style={payStyles.planRadioDot}/>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          )}

          {/* Step 3 – account details (step 2 when the plan step is hidden) */}
          <div style={payStyles.step}>
            <div style={payStyles.stepHead}>
              <div style={payStyles.stepNum}>{accountStepNum}</div>
              <div>
                <div style={payStyles.stepTitle}>{t.payStep3Title}</div>
                <div style={payStyles.stepSub}>{t.payStep3Sub}</div>
              </div>
            </div>
            <div className="ssi-pay__fields" style={payStyles.fields}>
              <div style={payStyles.field}>
                <label style={payStyles.fieldLabel}>{t.payEmailLabel}</label>
                <input type="email" placeholder={t.payEmailPlaceholder} style={payStyles.fieldInput} value={email} onChange={e => onEmailChange(e.target.value)} onBlur={onEmailBlur}/>
                {emailStatus === 'checking' && <div style={payStyles.emailChecking}>Checking…</div>}
              </div>
            </div>
          </div>

          {/* Step 4 + submit — injected by the route once a Checkout Session
           * client_secret is available. See routes/Pay.jsx. */}
          {checkoutSlot}
        </div>

        {/* RIGHT – the trial summary / trust panel */}
        <aside className="ssi-pay__right" style={payStyles.right}>
          <div className="ssi-pay__summaryCard" style={payStyles.summaryCard}>
            <div style={payStyles.summaryKicker}>{t.paySummaryKicker}</div>
            <h3 style={payStyles.summaryTitle}>
              {lang ? `${t.paySummaryFor} ${lang.name}` : t.paySummaryNoLang}
            </h3>

            <div style={payStyles.timeline}>
              <div style={payStyles.tlRow}>
                <div style={payStyles.tlDotFilled}>1</div>
                <div>
                  <div style={payStyles.tlTitle}>{t.payTl1Title}</div>
                  <div style={payStyles.tlBody}>{t.payTl1Body}</div>
                </div>
              </div>
              <div style={payStyles.tlRow}>
                <div style={payStyles.tlDotFilled}>2</div>
                <div>
                  <div style={payStyles.tlTitle}>{t.payTl2Title}</div>
                  <div style={payStyles.tlBody}>{t.payTl2Body}</div>
                </div>
              </div>
              <div style={payStyles.tlRow}>
                <div style={payStyles.tlDotFilled}>3</div>
                <div>
                  <div style={payStyles.tlTitle}>{tl3Title}</div>
                  <div style={payStyles.tlBody}>{tl3Body}</div>
                </div>
              </div>
            </div>

            <div style={payStyles.includes}>
              <div style={payStyles.includesTitle}>{t.payIncludesTitle}</div>
              <ul className="pay-includes" style={payStyles.includesList}>
                <li style={payStyles.includesItem}>{t.payIncl1}</li>
                <li style={payStyles.includesItem}>{t.payIncl2}</li>
                <li style={payStyles.includesItem}>{t.payIncl3}</li>
                <li style={payStyles.includesItem}>{t.payIncl4}</li>
              </ul>
            </div>

            <div style={payStyles.quote}>
              <div style={payStyles.quoteText}>{t.payQuote}</div>
              <div style={payStyles.quoteAttr}>{t.payQuoteAttr}</div>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}

// Shown in the step 4 slot when the email is linked to an account that already has access.
export function PayAlreadyHasAccess({ t }) {
  return (
    <div style={payStyles.step}>
      <div style={payStyles.hasAccessBox}>
        <div style={payStyles.hasAccessTitle}>{t.payHasAccessTitle}</div>
        <p style={payStyles.hasAccessMsg}>{t.payHasAccessMsg}</p>
        <Link to="/app" style={payStyles.hasAccessCta}>{t.payHasAccessCta} →</Link>
      </div>
    </div>
  );
}

// Rendered once a Checkout Session client_secret is available.
// Calls stripe.initCheckoutElementsSdk() imperatively and mounts the
// PaymentElement to a div ref — the React wrappers (CheckoutProvider /
// useCheckout) are not yet in the released @stripe/react-stripe-js package.
// On submit, sdk.loadActions() → actions.confirm() finalises the session and
// Stripe redirects to the return_url configured by the backend.
export function PayCheckoutForm({ t, courseIdx, clientSecret, email, stepNum = 4 }) {
  const containerRef = useRef(null);
  const sdkRef = useRef(null);
  const [elementReady, setElementReady] = useState(false);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);

  useEffect(() => {
    if (!clientSecret || !containerRef.current) return;

    let cancelled = false;
    let paymentElement = null;

    stripePromise.then(stripe => {
      if (!stripe || cancelled) return;

      const sdk = stripe.initCheckoutElementsSdk({ clientSecret });
      sdkRef.current = sdk;

      paymentElement = sdk.createPaymentElement();
      paymentElement.on('ready', () => { if (!cancelled) setElementReady(true); });
      paymentElement.mount(containerRef.current);
    });

    return () => {
      cancelled = true;
      sdkRef.current = null;
      setElementReady(false);
      setError(null);
      if (paymentElement) paymentElement.unmount();
    };
  }, [clientSecret]);

  const handleSubmit = async () => {
    if (courseIdx < 0 || !sdkRef.current) return;
    setProcessing(true);
    setError(null);

    const loadResult = await sdkRef.current.loadActions();
    if (loadResult.type === 'error') {
      setError(loadResult.error.message);
      setProcessing(false);
      return;
    }

    const result = await loadResult.actions.confirm({ email });
    setProcessing(false);
    if (result.type === 'error') setError(result.error.message);
    // On success Stripe navigates to the backend-configured return_url.
  };

  const disabled = courseIdx < 0 || processing || !elementReady || !tosAccepted;

  return (
    <>
      <div style={payStyles.step}>
        <div style={payStyles.stepHead}>
          <div style={payStyles.stepNum}>{stepNum}</div>
          <div>
            <div style={payStyles.stepTitle}>{t.payStep4Title}</div>
            <div style={payStyles.stepSub}>{t.payStep4Sub}</div>
          </div>
        </div>
        <div className="ssi-pay__stripeFrame" style={payStyles.stripeFrame}>
          {/* Stripe mounts the PaymentElement into this div */}
          <div ref={containerRef}/>
          {error && <div style={payStyles.stripeError} role="alert">{error}</div>}
          <div style={payStyles.stripeFooter}>
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{marginRight:6,opacity:.65}}>
              <path d="M4 5V3.5a2 2 0 114 0V5m-5 0h6v5H3V5z" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {t.payStripeCredit}
          </div>
        </div>
      </div>
      <label style={{display:'flex', alignItems:'flex-start', gap:10, margin:'16px 0 4px', cursor:'pointer', fontSize:13.5, color:'var(--fg-2)', lineHeight:1.45}}>
        <input
          type="checkbox"
          checked={tosAccepted}
          onChange={e => setTosAccepted(e.target.checked)}
          style={{marginTop:2, flexShrink:0, accentColor:'var(--brandRed)'}}
        />
        {t.payTosLabel ?? 'I agree to the Terms of Service and Privacy Policy.'}
      </label>
      <button
        type="button"
        onClick={handleSubmit}
        disabled={disabled}
        style={{
          ...payStyles.cta,
          opacity: disabled ? .4 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}>
        {processing
          ? (t.payCtaProcessing ?? 'Processing…')
          : courseIdx < 0 ? t.payCtaEmpty : t.payCta}
        {!processing && courseIdx >= 0 && (
          <svg width="16" height="16" viewBox="0 0 14 14" fill="none" style={{marginLeft:8}}>
            <path d="M2 7h10m0 0L8 3m4 4L8 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </button>
      <div style={payStyles.ctaFine}>{t.payCtaFine}</div>
    </>
  );
}

// Offer v2 – card-less, Stripeless immediate access. The product definition
// (oneoff-month-trial) requires no checkout, so the CTA POSTs straight to the
// shared onestep endpoint with `email` in the body (c/s stay query params),
// then follows the returned redirect_url (response_type:"direct").
export function PayNoCardStart({ t, courseIdx, email, productId, variant, source }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [tosAccepted, setTosAccepted] = useState(false);

  const emailValid = email.trim().includes('@') && email.trim().includes('.');
  const disabled = courseIdx < 0 || !emailValid || !tosAccepted || submitting;

  const handleSubmit = async () => {
    if (disabled) return;
    setSubmitting(true);
    setError(null);
    try {
      const apiBase = import.meta.env.VITE_API_BASE;
      if (!apiBase) throw new Error('VITE_API_BASE is not configured.');
      const qs = new URLSearchParams({ c: PAY_LANGS[courseIdx].code });
      if (source) qs.set('s', source);
      if (variant) qs.set('v', variant);
      const r = await fetch(`${apiBase}/membership/onestep/${productId}?${qs}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      // 400 = email missing/invalid, or the user already has access.
      if (r.status === 400) {
        setError('We couldn’t start your free 30 days — that email may already have access, or it isn’t valid.');
        setSubmitting(false);
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json().catch(() => ({}));
      markHasAccount();
      window.location.assign(data.redirect_url || '/app');
    } catch (e) {
      setError('Could not start your free 30 days — please try again.');
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleSubmit}
        disabled={disabled}
        style={{
          ...payStyles.cta,
          opacity: disabled ? .4 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}>
        {submitting
          ? (t.payCtaProcessing ?? 'Setting up your account…')
          : courseIdx < 0 ? t.payCtaEmpty : t.payCta}
      </button>
      {error && <div style={{...payStyles.stripeError, textAlign:'center'}} role="alert">{error}</div>}
      <label style={{display:'flex', alignItems:'flex-start', gap:10, margin:'16px 0 4px', cursor:'pointer', fontSize:13.5, color:'var(--fg-2)', lineHeight:1.45}}>
        <input
          type="checkbox"
          checked={tosAccepted}
          onChange={e => setTosAccepted(e.target.checked)}
          style={{marginTop:2, flexShrink:0, accentColor:'var(--brandRed)'}}
        />
        {t.payTosLabel ?? 'I agree to the Terms of Service and Privacy Policy.'}
      </label>
      <div style={payStyles.ctaFine}>{t.payCtaFine}</div>
    </>
  );
}

// Shown in Step 4 while the client_secret is being fetched, when no language
// has been selected yet, or when the session request failed.
export function PayCheckoutLoading({ t, courseIdx, loading, error, onRetry, stepNum = 4 }) {
  return (
    <>
      <div style={payStyles.step}>
        <div style={payStyles.stepHead}>
          <div style={payStyles.stepNum}>{stepNum}</div>
          <div>
            <div style={payStyles.stepTitle}>{t.payStep4Title}</div>
            <div style={payStyles.stepSub}>{t.payStep4Sub}</div>
          </div>
        </div>
        <div className="ssi-pay__stripeFrame" style={{...payStyles.stripeFrame, minHeight:120, display:'flex', alignItems:'center', justifyContent:'center'}}>
          {error ? (
            <div style={{textAlign:'center'}}>
              <div style={{fontSize:13.5, color:'#DB1E17', marginBottom:10}}>{error}</div>
              <button type="button" onClick={onRetry} style={payStyles.retryBtn}>Try again</button>
            </div>
          ) : loading ? (
            <div style={payStyles.loadingDots}>
              <div style={payStyles.loadingDot}/>
              <div style={{...payStyles.loadingDot, animationDelay:'.15s'}}/>
              <div style={{...payStyles.loadingDot, animationDelay:'.3s'}}/>
            </div>
          ) : (
            <div style={{fontSize:13.5, color:'var(--grey)', textAlign:'center'}}>
              Select a language above to continue.
            </div>
          )}
        </div>
      </div>
      <button type="button" disabled style={{...payStyles.cta, opacity:.4, cursor:'not-allowed'}}>
        {courseIdx < 0 ? t.payCtaEmpty : t.payCta}
      </button>
      <div style={payStyles.ctaFine}>{t.payCtaFine}</div>
    </>
  );
}

const payStyles = {
  wrap: {
    background: 'var(--offWhite, #FDF5F5)',
    padding: '64px 32px 96px',
    minHeight: 'calc(100vh - 120px)',
  },
  shell: {
    maxWidth: 1200,
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: '1fr 420px',
    gap: 40,
    alignItems: 'flex-start',
  },

  left: {
    background: '#fff',
    borderRadius: 20,
    border: '1px solid rgba(30,20,18,.06)',
    boxShadow: '0 1px 2px rgba(0,0,0,.04), 0 12px 32px -16px rgba(73,3,0,.16)',
    padding: '40px 44px',
  },
  kicker: { fontSize: 12, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--brandRed)', fontWeight: 600, marginBottom: 12 },
  h1: { fontFamily: 'var(--font-display)', fontSize: 40, lineHeight: 1.08, margin: '0 0 14px', letterSpacing: '-.01em' },
  lede: { fontSize: 16, color: 'var(--grey)', lineHeight: 1.55, margin: '0 0 40px' },

  couponBanner: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, background: 'rgba(39,153,106,.08)', border: '1.5px dashed rgba(39,153,106,.55)', borderRadius: 12, padding: '14px 18px', margin: '-16px 0 36px' },
  couponTag: { flexShrink: 0, background: '#27996A', color: '#fff', fontSize: 11.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', padding: '5px 10px', borderRadius: 999 },
  couponText: { fontSize: 14.5, color: 'var(--fg-1)', fontWeight: 500, lineHeight: 1.45 },

  step: { borderTop: '1px solid var(--border)', paddingTop: 28, marginTop: 28 },
  stepHead: { display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 18 },
  stepNum: {
    flexShrink: 0, width: 30, height: 30, borderRadius: '50%',
    background: 'var(--brandRed)', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600,
  },
  stepTitle: { fontFamily: 'var(--font-display)', fontSize: 22, lineHeight: 1.2, margin: '2px 0 4px' },
  stepSub: { fontSize: 13.5, color: 'var(--grey)', lineHeight: 1.4 },
  stepNote: { fontSize: 12.5, color: 'var(--grey)', marginTop: 16, textAlign: 'center', fontStyle: 'italic' },

  picker: { display: 'flex', flexWrap: 'wrap', gap: '18px 22px', justifyContent: 'flex-start', padding: '4px 0' },
  chip: { background: 'transparent', border: 'none', padding: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, fontFamily: 'inherit', cursor: 'pointer', transition: 'transform 160ms ease', minWidth: 64 },
  chipFlag: { width: 54, height: 54, borderRadius: '50%', overflow: 'hidden', background: 'var(--brandRed)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'box-shadow 160ms ease' },
  chipFlagImg: { width: '100%', height: '100%', objectFit: 'cover' },
  chipGlyph: { color: '#fff', fontFamily: 'var(--font-display)', fontSize: 13, lineHeight: 1, fontWeight: 500 },
  chipName: { fontSize: 12.5, transition: 'color 160ms ease' },

  planGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  planCard: { position: 'relative', textAlign: 'left', background: '#fff', border: '1.5px solid var(--border)', borderRadius: 14, padding: '20px 20px 18px', cursor: 'pointer', fontFamily: 'inherit', transition: 'border-color 160ms ease, box-shadow 160ms ease' },
  planTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  planName: { fontSize: 13, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--fg-1)' },
  planSave: { fontSize: 10, fontWeight: 600, letterSpacing: '.04em', background: 'var(--brandRed)', color: '#fff', padding: '3px 7px', borderRadius: 999, textTransform: 'uppercase' },
  planPrice: { display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 8 },
  planPriceNum: { fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 500, letterSpacing: '-.01em' },
  planPricePer: { fontSize: 13, color: 'var(--grey)' },
  planBlurb: { fontSize: 13, color: 'var(--grey)', lineHeight: 1.45, paddingRight: 30 },
  planRadio: { position: 'absolute', top: 20, right: 20, width: 20, height: 20, borderRadius: '50%', border: '1.5px solid var(--border-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'border-color 160ms ease' },
  planRadioDot: { width: 10, height: 10, borderRadius: '50%', background: 'var(--brandRed)' },

  fields: { display: 'grid', gap: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  fieldLabel: { fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--grey)', fontWeight: 600 },
  fieldInput: { padding: '12px 14px', borderRadius: 10, border: '1px solid var(--border-strong)', fontFamily: 'inherit', fontSize: 14.5, background: '#fff', color: 'var(--fg-1)', outline: 'none' },
  emailChecking: { fontSize: 12, color: 'var(--grey)', marginTop: 5, fontStyle: 'italic' },

  hasAccessBox:   { background: 'rgba(39,153,106,.07)', border: '1px solid rgba(39,153,106,.3)', borderRadius: 12, padding: '20px 22px' },
  hasAccessTitle: { fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 500, color: 'var(--fg-1)', marginBottom: 8 },
  hasAccessMsg:   { fontSize: 13.5, color: 'var(--grey)', lineHeight: 1.55, margin: '0 0 16px' },
  hasAccessCta:   { display: 'inline-block', background: 'var(--brandRed)', color: '#fff', padding: '12px 20px', borderRadius: 10, fontSize: 14.5, fontWeight: 600, textDecoration: 'none' },

  stripeFrame: { background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px 14px', boxShadow: '0 1px 2px rgba(0,0,0,.04)' },
  stripeFooter: { display: 'flex', alignItems: 'center', fontSize: 11.5, color: 'var(--grey)', paddingTop: 10, marginTop: 12, borderTop: '1px solid var(--border)' },
  stripeError: { fontSize: 13, color: '#DB1E17', marginTop: 10, lineHeight: 1.4 },

  loadingDots: { display: 'flex', gap: 6, alignItems: 'center' },
  loadingDot: { width: 8, height: 8, borderRadius: '50%', background: 'var(--brandRed)', animation: 'ssi-dot-pulse 1.2s ease-in-out infinite' },
  retryBtn: { fontSize: 13, fontWeight: 600, color: 'var(--brandRed)', background: 'transparent', border: '1.5px solid var(--brandRed)', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontFamily: 'inherit' },

  cta: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--brandRed)', color: '#fff', padding: '18px 26px', borderRadius: 12, fontSize: 16, fontWeight: 600, border: 0, fontFamily: 'inherit', boxShadow: '0 6px 18px -6px rgba(178,25,45,.55)', width: '100%', marginTop: 36, transition: 'opacity 160ms ease' },
  ctaFine: { fontSize: 12.5, color: 'var(--grey)', textAlign: 'center', marginTop: 14, lineHeight: 1.5 },

  right: { position: 'sticky', top: 32 },
  summaryCard: { background: '#fff', borderRadius: 20, border: '1px solid rgba(30,20,18,.06)', boxShadow: '0 1px 2px rgba(0,0,0,.04), 0 12px 32px -16px rgba(73,3,0,.16)', padding: '32px 32px 28px' },
  summaryKicker: { fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--brandRed)', fontWeight: 600, marginBottom: 8 },
  summaryTitle: { fontFamily: 'var(--font-display)', fontSize: 26, lineHeight: 1.15, margin: '0 0 24px' },
  timeline: { borderLeft: '2px solid rgba(178,25,45,.2)', paddingLeft: 0, display: 'flex', flexDirection: 'column', gap: 18, margin: '0 0 28px' },
  tlRow: { display: 'grid', gridTemplateColumns: '28px 1fr', gap: 12, alignItems: 'flex-start', marginLeft: -14 },
  tlDotFilled: { width: 28, height: 28, borderRadius: '50%', background: '#fff', border: '2px solid var(--brandRed)', color: 'var(--brandRed)', fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  tlTitle: { fontSize: 14.5, fontWeight: 600, color: 'var(--fg-1)', marginBottom: 2, lineHeight: 1.3 },
  tlBody: { fontSize: 13, color: 'var(--grey)', lineHeight: 1.5 },

  includes: { borderTop: '1px solid var(--border)', paddingTop: 18, marginBottom: 22 },
  includesTitle: { fontSize: 11, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--grey)', fontWeight: 600, marginBottom: 10 },
  includesList: { listStyle: 'none', margin: 0, padding: 0 },
  includesItem: { fontSize: 13.5, color: 'var(--fg-1)', lineHeight: 1.4, padding: '6px 0 6px 22px', position: 'relative' },

  quote: { borderTop: '1px solid var(--border)', paddingTop: 18, marginTop: 6 },
  quoteText: { fontFamily: 'var(--font-display)', fontSize: 15.5, lineHeight: 1.5, fontStyle: 'italic', color: 'var(--fg-1)', marginBottom: 10 },
  quoteAttr: { fontSize: 12, color: 'var(--grey)', letterSpacing: '.02em' },
};

// Tick marker on includes list items, plus dot-pulse keyframes for the
// loading animation in PayCheckoutLoading.
export function PayTickStyleInjector() {
  useEffect(() => {
    if (document.getElementById('pay-tick-style')) return;
    const s = document.createElement('style');
    s.id = 'pay-tick-style';
    s.textContent = `
      .pay-includes li::before { content:''; position:absolute; left:0; top:11px; width:12px; height:12px; border-radius:50%; background: rgba(178,25,45,.12); }
      .pay-includes li::after { content:''; position:absolute; left:3px; top:14px; width:6px; height:3px; border-left:1.5px solid var(--brandRed); border-bottom:1.5px solid var(--brandRed); transform: rotate(-45deg); }
      @keyframes ssi-dot-pulse { 0%,80%,100%{transform:scale(0);opacity:.4} 40%{transform:scale(1);opacity:1} }
    `;
    document.head.appendChild(s);
  }, []);
  return null;
}
